import is from '@sindresorhus/is';
import type { QueryClient, QueryKey } from '@tanstack/react-query';

import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';
import type { JobRole } from '~/shared/data-contract/job-role';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';

import { jobArchitectureKeys } from '../query-keys';

type ReviewAction = 'review' | 'unreview';

type UpdateVersionsDataParams = {
    readonly action: ReviewAction;
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly queryClient: QueryClient;
    readonly version: string;
};

const updateVersionItem = (
    versionItem: JobProfileVersions[number],
    version: string,
    action: ReviewAction,
): JobProfileVersions[number] => {
    if (versionItem.version === Number(version)) {
        const updatedReviewer = !is.nullOrUndefined(versionItem.reviewer)
            ? {
                  ...versionItem.reviewer,
                  state: action === 'review' ? ('approved' as const) : ('assigned' as const),
              }
            : versionItem.reviewer;

        return {
            ...versionItem,
            reviewedAt: action === 'review' ? new Date().toISOString() : null,
            reviewer: updatedReviewer,
        };
    }

    return versionItem;
};

export const updateVersionsData = ({
    action,
    architectureId,
    itemId,
    itemType,
    queryClient,
    version,
}: UpdateVersionsDataParams): void => {
    const versionsQueryKey =
        itemType === 'job-level'
            ? jobArchitectureKeys.levelVersions(architectureId, itemId)
            : jobArchitectureKeys.roleVersions(architectureId, itemId);

    queryClient.setQueryData<JobProfileVersions>(versionsQueryKey, (old) => {
        if (old === null || old === undefined) {
            return old;
        }

        return old.map((versionItem) => updateVersionItem(versionItem, version, action));
    });
};

type UpdateRoleDetailsParams = {
    readonly action: ReviewAction;
    readonly architectureId: string;
    readonly itemId: string;
    readonly myUserProfileData?: { readonly id: string; readonly firstName?: string; readonly lastName?: string };
    readonly queryClient: QueryClient;
    readonly version: string;
};

const updateRoleDetailsQueries = (
    architectureId: string,
    itemId: string,
    version: string,
    action: ReviewAction,
    queryClient: QueryClient,
): void => {
    const roleDetailsQueries = queryClient.getQueriesData({
        queryKey: jobArchitectureKeys.role(architectureId, itemId),
    });

    const isReviewed = action === 'review';

    roleDetailsQueries.forEach(([queryKey, data]: [QueryKey, JobRoleDetails]) => {
        if (!is.undefined(data) && !is.undefined(data.versionInfo)) {
            if (data.versionInfo.version === Number(version)) {
                queryClient.setQueryData(queryKey, {
                    ...data,
                    actions: action === 'review' ? { ...data.actions, canPublish: true } : data.actions,
                    versionInfo: {
                        ...data.versionInfo,
                        isReviewed,
                    },
                });
            }
        }
    });
};

const createReviewerFromUserProfile = (
    myUserProfileData: { readonly id: string; readonly firstName?: string; readonly lastName?: string },
    action: ReviewAction,
): {
    readonly id: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly state: 'approved' | 'assigned';
} => {
    return {
        id: myUserProfileData.id,
        firstName: myUserProfileData.firstName ?? '',
        lastName: myUserProfileData.lastName ?? '',
        state: action === 'review' ? ('approved' as const) : ('assigned' as const),
    };
};

const updateRoleInList = (
    role: JobRole,
    itemId: string,
    version: string,
    action: ReviewAction,
    myUserProfileData?: { readonly id: string; readonly firstName?: string; readonly lastName?: string },
): JobRole | undefined => {
    if (role.id === itemId && !is.undefined(role.versionInfo)) {
        if (role.versionInfo.version === Number(version)) {
            return {
                ...role,
                reviewer: !is.nullOrUndefined(myUserProfileData)
                    ? createReviewerFromUserProfile(myUserProfileData, action)
                    : role.reviewer,
                versionInfo: {
                    ...role.versionInfo,
                    isReviewed: action === 'review',
                },
            };
        }
    }

    return undefined;
};

const updateRolesListQueries = (
    architectureId: string,
    itemId: string,
    version: string,
    action: ReviewAction,
    myUserProfileData: UpdateRoleDetailsParams['myUserProfileData'],
    queryClient: QueryClient,
): JobRole | undefined => {
    const rolesListQueries = queryClient.getQueriesData({
        queryKey: jobArchitectureKeys.roles(architectureId),
    });

    let foundRole: JobRole | undefined;

    rolesListQueries.forEach(([queryKey, data]: [QueryKey, readonly JobRole[]]) => {
        if (!is.undefined(data) && is.array(data)) {
            const updatedRoles = data.map((role) => {
                const updatedRole = updateRoleInList(role, itemId, version, action, myUserProfileData);

                if (!is.undefined(updatedRole)) {
                    if (is.undefined(foundRole)) {
                        foundRole = updatedRole;
                    }

                    return updatedRole;
                }

                return role;
            });

            queryClient.setQueryData(queryKey, updatedRoles);
        }
    });

    return foundRole;
};

export const updateRoleDetails = ({
    action,
    architectureId,
    itemId,
    myUserProfileData,
    queryClient,
    version,
}: UpdateRoleDetailsParams): JobRole | undefined => {
    updateRoleDetailsQueries(architectureId, itemId, version, action, queryClient);

    return updateRolesListQueries(architectureId, itemId, version, action, myUserProfileData, queryClient);
};

type UpdateLevelDetailsParams = {
    readonly action: ReviewAction;
    readonly architectureId: string;
    readonly itemId: string;
    readonly queryClient: QueryClient;
    readonly version: string;
};

const updateLevelDetailsQueries = (
    architectureId: string,
    itemId: string,
    version: string,
    action: ReviewAction,
    queryClient: QueryClient,
): void => {
    const levelDetailsQueries = queryClient.getQueriesData({
        queryKey: jobArchitectureKeys.level(architectureId, itemId),
    });

    const isReviewed = action === 'review';

    levelDetailsQueries.forEach(([queryKey, data]: [QueryKey, JobLevelDetails]) => {
        if (!is.undefined(data) && !is.undefined(data.versionInfo)) {
            if (data.versionInfo.version === Number(version)) {
                queryClient.setQueryData(queryKey, {
                    ...data,
                    actions: action === 'review' ? { ...data.actions, canPublish: true } : data.actions,
                    versionInfo: {
                        ...data.versionInfo,
                        isReviewed,
                    },
                });
            }
        }
    });
};

const updateLevelInList = (
    level: JobLevel,
    itemId: string,
    version: string,
    action: ReviewAction,
): JobLevel | undefined => {
    if (level.id === itemId && !is.undefined(level.versionInfo)) {
        if (level.versionInfo.version === Number(version)) {
            return {
                ...level,
                versionInfo: {
                    ...level.versionInfo,
                    isReviewed: action === 'review',
                },
            };
        }
    }

    return undefined;
};

const updateLevelsListQueries = (
    architectureId: string,
    itemId: string,
    version: string,
    action: ReviewAction,
    queryClient: QueryClient,
): JobLevel | undefined => {
    const levelsListQueries = queryClient.getQueriesData({
        queryKey: jobArchitectureKeys.levels(architectureId),
    });

    let foundLevel: JobLevel | undefined;

    levelsListQueries.forEach(([queryKey, data]: [QueryKey, readonly JobLevel[]]) => {
        if (!is.undefined(data) && is.array(data)) {
            const updatedLevels = data.map((level) => {
                const updatedLevel = updateLevelInList(level, itemId, version, action);

                if (!is.undefined(updatedLevel)) {
                    if (is.undefined(foundLevel)) {
                        foundLevel = updatedLevel;
                    }

                    return updatedLevel;
                }

                return level;
            });

            queryClient.setQueryData(queryKey, updatedLevels);
        }
    });

    return foundLevel;
};

export const updateLevelDetails = ({
    action,
    architectureId,
    itemId,
    queryClient,
    version,
}: UpdateLevelDetailsParams): JobLevel | undefined => {
    updateLevelDetailsQueries(architectureId, itemId, version, action, queryClient);

    return updateLevelsListQueries(architectureId, itemId, version, action, queryClient);
};
